<script setup lang="ts">
import type {
	VideoClickMetadata,
	VideoImpressionDataItem,
	VideoImpressionMetadata,
} from '@laam/cathode-events/src/events';
import { type ProductContentsResponseSchema } from '@laam/cms-shared';
import { VideoCard } from '@laam/ui/base';
import { PhPlay } from '@phosphor-icons/vue';
import { vElementVisibility } from '@vueuse/components';
import { useWindowScroll, watchDebounced, watchOnce } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { nextTick, onMounted, ref } from 'vue';
import { useCurrency } from '~/stores/currency';
import { useGlobalStore } from '~/stores/global';
import { isElementVisible } from '~/utils/helpers';
import { useDeviceType } from '~/utils/use-device-type';

interface ProductVideoCardProps {
	data: ProductContentsResponseSchema['contents'][number];
	componentInfo?: {
		heading?: string;
		component?: string;
	};
	page: string;
	horizontalIndex: number;
	verticalIndex?: number;
}

const props = defineProps<ProductVideoCardProps>();
const elementRef = ref<HTMLElement>();
const CurrencyStore = useCurrency();

const { currencySymbol } = storeToRefs(CurrencyStore);
const { getFormattedPrice } = CurrencyStore;
const { $cathodeClient } = useNuxtApp();
const { deviceType } = useDeviceType();

const formattedPrice = computed(() => {
	const priceValue = props.data.product_variants_min_price ?? 0;
	return getFormattedPrice(priceValue, true);
});

const isVisible = ref(false);
const video = ref<HTMLVideoElement | null>(null);
function onElementVisibility(state: boolean) {
	isVisible.value = state;
}

// const playVideo = async () => {
// 	if (!video.value) return;
// 	try {
// 		await video.value.play();
// 	} catch (error) {
// 		console.error('Error playing video:', error);
// 	}
// };

// const pauseVideo = () => {
// 	if (!video.value) return;

// 	video.value.pause();
// };

watchDebounced(
	isVisible,
	(value) => {
		if (value) {
			const impressionDataItem: VideoImpressionDataItem = {
				horizontal_index: props.horizontalIndex,
				video_url:
					props.data.videos?.[0]?.hls_url ||
					props.data.videos?.[0]?.original_url ||
					null,
				product_id: props.data.product_id || null,
				content_id: props.data.content_id || null,
				inventory:
					props.data.product_total_sellable_inventory_on_store?.[
						String(globalStore.store.id)
					] ?? null,
			};

			const metadata: VideoImpressionMetadata = {
				component: props.componentInfo?.heading || null,
				data: [impressionDataItem],
				page: props.page,
				source: window.location.href,
				type: hasWindowScrolled.value ? 'scroll_impression' : 'load_impression',
				vertical_index: props.verticalIndex || null,
				store_id: globalStore.store.id || null,
			};
			$cathodeClient.queueImpression('video_impression', metadata);
		}
	},
	{
		debounce: useRuntimeConfig().public.impressionDebounceTime,
	},
);

const { y } = useWindowScroll();
const hasWindowScrolled = ref(false);

watchOnce(y, () => {
	hasWindowScrolled.value = true;
});

const globalStore = useGlobalStore();

onMounted(() => {
	nextTick(() => {
		if (!elementRef.value) return;
		const element = elementRef.value as HTMLElement;
		isVisible.value = isElementVisible(element);
	});
});

function handleClickEvent() {
	const metadata: VideoClickMetadata = {
		component: props.componentInfo?.heading || null,
		horizontal_index: props.horizontalIndex,
		video_url:
			props.data.videos?.[0]?.hls_url ||
			props.data.videos?.[0]?.original_url ||
			null,
		page: props.page,
		source: window.location.href,
		vertical_index: props.verticalIndex || null,
		product_id: props.data.product_id || null,
		content_id: props.data.content_id || null,
		store_id: globalStore.store.id,
		inventory:
			props.data.product_total_sellable_inventory_on_store?.[
				String(globalStore.store.id)
			] || null,
	};

	$cathodeClient.queueEvent('video_click', metadata);
}
</script>

<template>
	<div
		ref="elementRef"
		v-element-visibility="[onElementVisibility, { threshold: 0.8 }]"
		@click="handleClickEvent()"
	>
		<VideoCard
			:brand-name="props.data.brand"
			:shopify-id="props.data.product_shopify_id"
			:price="formattedPrice"
			:currency-symbol="currencySymbol"
		>
			<template #video>
				<video
					ref="video"
					class="rounded-sm"
					:class="{ 'rounded-xl': deviceType === 'mobile' }"
					:video-url="props.data.videos?.[0]?.original_url || ''"
					:src="props.data.videos?.[0]?.original_url || ''"
					:poster="props.data.videos ? props.data.videos[0]?.thumbnail : ''"
				></video>
			</template>

			<template #top-left>
				<div
					class="flex items-center justify-center w-7xl h-7xl bg-black bg-opacity-30 rounded-full"
					:class="{
						'!w-5xl !h-5xl': deviceType === 'mobile',
					}"
				>
					<PhPlay :size="10" weight="fill" :color="'#ffffff'" />
				</div>
			</template>
		</VideoCard>
	</div>
</template>
